import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Boxes,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  RotateCcw,
  Timer,
  UserRound,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  cn,
} from '../../ui';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { useDailyScrum } from '../../features/sprintboard/useDailyScrum';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';
import {
  DEFAULT_PER_PERSON_SECONDS,
  buildStandupQueue,
  clampPerPersonSeconds,
  elapsedFraction,
  formatDuration,
  isLastIndex,
  isTimeUp,
  nextIndex,
  previousIndex,
  remainingSeconds,
  type StandupParticipant,
} from '../../features/sprintboard/standup/standupTimerModel';
import type { ScrumTaskEntry } from '../../features/sprintboard/scrum/dailyScrumModel';

/** One task line in the current speaker's update. */
function StandupEntry({ entry }: { entry: ScrumTaskEntry }) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-zinc-100">{entry.taskTitle}</span>
        {entry.blocked && (
          <Badge tone="danger" dot>
            ⚠ Blocked
          </Badge>
        )}
      </div>
      <dl className="mt-1.5 space-y-1 text-xs">
        {entry.completed && (
          <div className="flex gap-1.5">
            <dt className="w-16 shrink-0 font-medium text-emerald-400">Did</dt>
            <dd className="text-zinc-300">{entry.completed}</dd>
          </div>
        )}
        {entry.next && (
          <div className="flex gap-1.5">
            <dt className="w-16 shrink-0 font-medium text-sky-400">Next</dt>
            <dd className="text-zinc-300">{entry.next}</dd>
          </div>
        )}
        {entry.blocker && (
          <div className="flex gap-1.5">
            <dt className="w-16 shrink-0 font-medium text-red-400">Blocker</dt>
            <dd className="text-zinc-300">{entry.blocker}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

/**
 * StandupPage — a focused, TIME-BOXED standup driven by the Daily Scrum
 * projection. It steps through a per-member queue one speaker at a time, showing
 * that member's concise update at a glance, with Next / Previous controls, a
 * per-person countdown timer (configurable, default 120s) that auto-advances (or
 * flags time-up on the last speaker), and a total elapsed standup timer with
 * Start / Pause / Reset.
 *
 * It is a READ/roll-up view (no editing) — it reuses {@link useDailyScrum} (the
 * standup data layer) + {@link usePeopleDirectory}; the ticking uses
 * useEffect + setInterval and cleans the interval up on unmount/pause. The pure
 * timer/queue math lives in {@link ../../features/sprintboard/standup/standupTimerModel}.
 */
export function StandupPage() {
  const navigate = useNavigate();
  const { activeRoom, activeRoomId } = useActiveRoom();
  const { groups, updates, loading, error } = useDailyScrum();
  const { people } = usePeopleDirectory();

  const queue = useMemo<StandupParticipant[]>(
    () => buildStandupQueue(groups, people),
    [groups, people],
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [running, setRunning] = useState(false);
  const [personElapsed, setPersonElapsed] = useState(0);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [budgetText, setBudgetText] = useState(String(DEFAULT_PER_PERSON_SECONDS));

  const perPersonSeconds = clampPerPersonSeconds(
    Number(budgetText) || DEFAULT_PER_PERSON_SECONDS,
  );

  // Keep the active index within bounds if the queue shrinks after a reload.
  const safeIndex = queue.length > 0 ? Math.min(currentIndex, queue.length - 1) : 0;
  const current = queue[safeIndex];
  const remaining = remainingSeconds(perPersonSeconds, personElapsed);
  const timeUp = isTimeUp(perPersonSeconds, personElapsed);

  // Ticking: one interval while running; ALWAYS cleaned up on pause/unmount.
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      setPersonElapsed((e) => e + 1);
      setTotalElapsed((t) => t + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [running]);

  // Auto-advance when the current speaker's budget is exhausted. On the last
  // speaker we pause instead (visually flagging time-up), never looping.
  useEffect(() => {
    if (!running) return;
    if (!isTimeUp(perPersonSeconds, personElapsed)) return;
    if (isLastIndex(safeIndex, queue.length)) {
      setRunning(false);
    } else {
      setCurrentIndex(nextIndex(safeIndex, queue.length));
      setPersonElapsed(0);
    }
  }, [personElapsed, running, perPersonSeconds, safeIndex, queue.length]);

  // Advancing controls reset the per-person clock but keep the total running.
  const goNext = () => {
    setCurrentIndex((i) => nextIndex(Math.min(i, queue.length - 1), queue.length));
    setPersonElapsed(0);
  };
  const goPrev = () => {
    setCurrentIndex((i) => previousIndex(Math.min(i, queue.length - 1)));
    setPersonElapsed(0);
  };
  const jumpTo = (index: number) => {
    setCurrentIndex(index);
    setPersonElapsed(0);
  };
  const reset = () => {
    setRunning(false);
    setCurrentIndex(0);
    setPersonElapsed(0);
    setTotalElapsed(0);
  };

  if (!activeRoomId) {
    return (
      <div>
        <PageHeader
          icon={<Timer className="h-5 w-5" />}
          title="Standup"
          description="A focused, time-boxed standup driven by the daily scrum."
        />
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="No active room"
          description="Standup rolls up a room's task updates. Select or create a room first."
          action={<Button onClick={() => navigate('/rooms')}>Go to Rooms</Button>}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={<Timer className="h-5 w-5" />}
        title="Standup"
        description={
          <span className="inline-flex items-center gap-1.5">
            Time-boxed standup for
            <Badge tone="accent">{activeRoom?.name ?? 'active room'}</Badge>
          </span>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading standup" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load the standup"
          description={error}
        />
      ) : queue.length === 0 ? (
        <EmptyState
          icon={<Timer className="h-6 w-6" />}
          title="No one to stand up"
          description={
            updates.length === 0
              ? 'No task updates and no team members yet. Post updates or add a team to build the queue.'
              : 'No participants found for this standup.'
          }
        />
      ) : (
        <div className="space-y-4">
          {/* Timer + controls */}
          <Card>
            <CardBody className="space-y-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-baseline gap-3">
                  <span
                    className={cn(
                      'font-mono text-4xl font-semibold tabular-nums',
                      timeUp ? 'text-red-400' : 'text-zinc-100',
                    )}
                    aria-live="polite"
                  >
                    {formatDuration(remaining)}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {timeUp ? "time's up" : 'remaining'} · person{' '}
                    {safeIndex + 1} of {queue.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<ChevronLeft className="h-3.5 w-3.5" />}
                    onClick={goPrev}
                    disabled={safeIndex === 0}
                  >
                    Prev
                  </Button>
                  {running ? (
                    <Button
                      size="sm"
                      leftIcon={<Pause className="h-3.5 w-3.5" />}
                      onClick={() => setRunning(false)}
                    >
                      Pause
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      leftIcon={<Play className="h-3.5 w-3.5" />}
                      onClick={() => setRunning(true)}
                    >
                      Start
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    rightIcon={<ChevronRight className="h-3.5 w-3.5" />}
                    onClick={goNext}
                    disabled={isLastIndex(safeIndex, queue.length)}
                  >
                    Next
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                    onClick={reset}
                  >
                    Reset
                  </Button>
                </div>
              </div>

              {/* Per-person progress bar */}
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={perPersonSeconds}
                aria-valuenow={Math.min(personElapsed, perPersonSeconds)}
              >
                <div
                  className={cn(
                    'h-full rounded-full transition-[width] duration-500',
                    timeUp ? 'bg-red-500' : 'bg-accent',
                  )}
                  style={{
                    width: `${elapsedFraction(perPersonSeconds, personElapsed) * 100}%`,
                  }}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="w-40">
                  <Input
                    label="Seconds per person"
                    type="number"
                    inputMode="numeric"
                    min={15}
                    max={600}
                    value={budgetText}
                    onChange={(e) => setBudgetText(e.target.value)}
                    onBlur={() => setBudgetText(String(perPersonSeconds))}
                    hint={`Default ${DEFAULT_PER_PERSON_SECONDS}s`}
                  />
                </div>
                <div className="text-right text-xs text-zinc-500">
                  <div>Total standup time</div>
                  <div className="font-mono text-lg tabular-nums text-zinc-200">
                    {formatDuration(totalElapsed)}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Current speaker */}
          {current && (
            <Card>
              <CardBody className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
                    <UserRound className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-zinc-100">
                        {current.name}
                      </span>
                      {current.hasUpdate ? (
                        <Badge tone="success">
                          {current.entries.length} update
                          {current.entries.length === 1 ? '' : 's'}
                        </Badge>
                      ) : (
                        <Badge tone="neutral">No update</Badge>
                      )}
                    </div>
                    <span className="text-xs text-zinc-500">@{current.alias}</span>
                  </div>
                </div>

                {current.hasUpdate ? (
                  <div className="space-y-2">
                    {current.entries.map((entry) => (
                      <StandupEntry
                        key={`${current.alias}-${entry.taskId}`}
                        entry={entry}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-surface-border bg-surface-1 px-3 py-6 text-center text-sm text-zinc-500">
                    No standup update posted for this sprint.
                  </p>
                )}
              </CardBody>
            </Card>
          )}

          {/* Queue overview */}
          <Card>
            <CardBody className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Queue
              </h3>
              <ol className="space-y-1.5">
                {queue.map((p, index) => (
                  <li key={p.alias}>
                    <button
                      type="button"
                      aria-current={index === safeIndex ? 'true' : undefined}
                      onClick={() => jumpTo(index)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                        index === safeIndex
                          ? 'border-accent bg-surface-3'
                          : 'border-surface-border bg-surface-1 hover:border-zinc-600',
                      )}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-5 shrink-0 text-xs tabular-nums text-zinc-500">
                          {index + 1}
                        </span>
                        <span className="truncate text-sm font-medium text-zinc-100">
                          {p.name}
                        </span>
                      </span>
                      {p.hasUpdate ? (
                        <Badge tone="info">{p.entries.length}</Badge>
                      ) : (
                        <span className="text-xs text-zinc-500">no update</span>
                      )}
                    </button>
                  </li>
                ))}
              </ol>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}

export default StandupPage;
